import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import {
  DEFAULT_FOOTER_CTA,
  resolveFooterCta,
  type FooterCtaCopy,
} from './footer-cta';

type FooterCtaContextValue = {
  copy: FooterCtaCopy;
  setOverride: (heading?: string | null, text?: string | null) => void;
  clearOverride: () => void;
};

const FooterCtaContext = createContext<FooterCtaContextValue | null>(null);

export const FooterCtaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [override, setOverrideState] = useState<Partial<FooterCtaCopy> | null>(
    null,
  );

  const setOverride = useCallback(
    (heading?: string | null, text?: string | null) => {
      setOverrideState({
        heading: heading?.trim() || undefined,
        text: text?.trim() || undefined,
      });
    },
    [],
  );

  const clearOverride = useCallback(() => {
    setOverrideState(null);
  }, []);

  const value = useMemo<FooterCtaContextValue>(
    () => ({
      copy: resolveFooterCta(override?.heading, override?.text),
      setOverride,
      clearOverride,
    }),
    [override?.heading, override?.text, setOverride, clearOverride],
  );

  return (
    <FooterCtaContext.Provider value={value}>{children}</FooterCtaContext.Provider>
  );
};

export function useFooterCtaCopy(): FooterCtaCopy {
  const ctx = useContext(FooterCtaContext);
  return ctx?.copy ?? DEFAULT_FOOTER_CTA;
}

export function useFooterCtaOverride(
  heading?: string | null,
  text?: string | null,
): void {
  const ctx = useContext(FooterCtaContext);
  const setOverride = ctx?.setOverride;
  const clearOverride = ctx?.clearOverride;

  useEffect(() => {
    if (!setOverride || !clearOverride) return undefined;

    const hasOverride = Boolean(heading?.trim() || text?.trim());
    if (!hasOverride) {
      clearOverride();
      return undefined;
    }

    setOverride(heading, text);
    return () => {
      clearOverride();
    };
  }, [setOverride, clearOverride, heading, text]);
}
